import axios from "axios";

// v2.51 — Smart backend URL resolution.
// Build-time `REACT_APP_BACKEND_URL` (CRA env var) bakes into the JS bundle.
// If the production build was made with a stale preview URL, every API call
// from the live domain (houseentry.in) goes to the preview backend → which
// is fork-specific and unreachable → user sees "LOGIN FAILED HTTP 404".
//
// Fix: when the page is served from the canonical production domain, ALWAYS
// use the same-origin URL (window.location.origin) regardless of what was
// baked at build time. Preview / dev / local envs continue to use the build-
// time env var.
const PROD_HOSTS = new Set(["houseentry.in", "www.houseentry.in"]);

function resolveBackendUrl() {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (PROD_HOSTS.has(host)) {
      // Same-origin → no CORS, no stale-build issue
      return window.location.origin;
    }
  }
  return process.env.REACT_APP_BACKEND_URL || "";
}

export const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  // 60s timeout — large enough for image uploads, small enough to fail fast on dead networks.
  // Without this, slow mobile retries can hang indefinitely and confuse users.
  timeout: 60000,
  // v2.62 — XSS hardening: send the HTTPOnly `he_session` cookie on every request.
  // Cookie is the source of truth for auth going forward; localStorage is being phased out.
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  // v2.62 — During the localStorage→cookie migration, still attach Bearer header
  // if a stale token exists. Backend will accept either, AND on a successful
  // /auth/me call it will install the cookie automatically — at which point the
  // response interceptor below clears localStorage so we stop sending the header.
  const token = localStorage.getItem("auth_token");
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  return config;
});

// v2.49 — Production stability fix.
// Background: when the backend rotates JWT_SECRET (re-deploy with a different
// secret, or a fresh pod with empty env), every previously-issued token
// instantly fails verification → backend returns 401 on every protected call.
// Before this interceptor, the frontend kept the dead token in localStorage
// indefinitely and every subsequent dashboard fetch silently 401'd, leaving
// the user staring at a half-rendered "broken" page. The interceptor below:
//   1. Drops the dead token so the next page load reads `null`
//   2. Redirects to /login ONLY for protected endpoints (not /auth/login itself,
//      otherwise a wrong-password attempt would loop the user)
//   3. Skips redirect for public read-only endpoints so anonymous browsing
//      isn't broken when a token randomly expires.
// v2.62 — `/auth/me` is now ALWAYS called on app mount (cookie-based auth means
// we can't peek at localStorage to skip the call). 401 here just means "not
// logged in" — do NOT bounce the user to /login.
// v2.71.82 — Public routes that NEVER trigger an auto-redirect on 401.
// A 401 here means "you're not logged in" — that's fine for browsing public
// content. Callers (`fetchSavedState`, `recordHeartbeat`, etc.) get the
// rejected promise and can react with a soft prompt, but the GUEST stays
// on the page they were reading.
//
// Why this matters
// ────────────────
// The dashboard / property detail / projects pages are publicly browseable.
// If any background API call returns 401 (session expired during a long
// read, request to a partially-protected endpoint, server token rotation),
// the old interceptor `window.location.replace("/login")`-ed the guest
// instantly — wiping their context and forcing them to re-navigate. Worse,
// the 401 still propagated as a rejected promise → upstream `.then()`
// without `.catch()` → unhandled rejection → ErrorBoundary trigger →
// "Something went wrong".
//
// Now: redirects fire ONLY when the CURRENT page is in the protected
// allowlist (dashboards, account, /me/*). Everywhere else, 401s are
// logged + the promise rejects + the page stays put.
const PROTECTED_PATH_PREFIXES = [
  "/admin",
  "/dealer",
  "/builder",
  "/team",
  "/account",
  "/me",
  "/profile",
  "/owner",
  "/caller",
];
const isProtectedPage = (pathname = "") =>
  PROTECTED_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));

const PUBLIC_PATHS = ["/auth/login", "/auth/register", "/auth/me", "/team/me", "/auth/magic-link", "/auth/magic-consume", "/auth/forgot-password", "/auth/reset-password", "/auth/team-otp/", "/auth/team-login", "/auth/logout"];
const isPublicAuth = (url = "") => PUBLIC_PATHS.some((p) => url.includes(p));

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const method = (error?.config?.method || "get").toUpperCase();

    // v2.71.82 — Always log the failing endpoint so future debugging is
    // trivial. Quiet for the routine /auth/me unauth probe.
    if (status && !(status === 401 && isPublicAuth(url))) {
      // eslint-disable-next-line no-console
      console.warn(`[api] ${method} ${url} → ${status}${error?.response?.data?.detail ? " · " + error.response.data.detail : ""}`);
    }

    if (status === 401 && !isPublicAuth(url)) {
      // Token is dead → clear it.
      try { localStorage.removeItem("auth_token"); } catch (_) {}
      if (typeof window !== "undefined") {
        const path = window.location.pathname;
        const isAuthScreen = path === "/login" || path === "/register" || path === "/access" || path.startsWith("/magic") || path === "/team-login" || path === "/forgot-password" || path === "/reset-password";
        // v2.71.82 — KEY CHANGE: only redirect when the user is on a
        // protected (dashboard-style) page. On public pages we just emit
        // an event so the page can soft-prompt with a sign-in modal.
        if (!isAuthScreen && isProtectedPage(path)) {
          const next = encodeURIComponent(path + (window.location.search || ""));
          const isTeamContext = path === "/team" || path.startsWith("/team/") || url.includes("/team/") || url.includes("/team-login");
          const loginPath = isTeamContext ? "/team-login" : "/login";
          window.location.replace(`${loginPath}?next=${next}`);
        } else if (!isAuthScreen) {
          // Public page → fire a soft event. Listeners (PropertyDetail,
          // Dashboard) can choose to open a sign-in modal IF the user just
          // tried a protected action. Background probes simply log and
          // move on without interrupting reading.
          try {
            window.dispatchEvent(new CustomEvent("he:auth-required", {
              detail: { url, method, status },
            }));
          } catch (_) { /* CustomEvent unavailable — silent */ }
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Indian price formatting — Lakhs / Crores (trims trailing zero: 1.20 Cr → 1.2 Cr)
export const formatINR = (n) => {
  if (!n && n !== 0) return "—";
  const trim = (v) => (v % 1 === 0 ? v.toFixed(0) : v.toFixed(2).replace(/\.?0+$/, ""));
  if (n >= 10000000) return `₹${trim(n / 10000000)} Cr`;
  if (n >= 100000) return `₹${trim(n / 100000)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
};

export const formatINRPlain = (n) =>
  `₹${(n || 0).toLocaleString("en-IN")}`;

// Parse "25 L", "1.2 Cr", "2500000" or {value, unit} into raw INR.
export const parseLC = (input) => {
  if (input === null || input === undefined || input === "") return 0;
  if (typeof input === "object" && input.value !== undefined) {
    const n = parseFloat(input.value);
    if (!Number.isFinite(n)) return 0;
    if (input.unit === "Cr") return Math.round(n * 10000000);
    if (input.unit === "L") return Math.round(n * 100000);
    return Math.round(n);
  }
  const s = String(input).trim().toLowerCase();
  const num = parseFloat(s);
  if (!Number.isFinite(num)) return 0;
  if (s.includes("cr")) return Math.round(num * 10000000);
  if (s.includes("l")) return Math.round(num * 100000);
  return Math.round(num);
};

// Inverse — convert raw INR to {value, unit} for the LC input UI.
export const splitLC = (raw) => {
  const n = Number(raw) || 0;
  if (n === 0) return { value: "", unit: "L" };
  if (n >= 10000000) {
    const v = n / 10000000;
    return { value: v % 1 === 0 ? String(v) : v.toFixed(2), unit: "Cr" };
  }
  return { value: String(Math.round(n / 100000 * 100) / 100), unit: "L" };
};

export const formatDistance = (m) => {
  if (!m && m !== 0) return "—";
  if (m < 1000) return `${m} m`;
  return `${(m / 1000).toFixed(1)} km`;
};

// v2.43 — Property area can be stored in any of these units (size_unit).
// v2.71.101 — Expanded unit handling: sqft / sqyd / sqm / acre. Display strings
// follow the user's requested format: "Sq Ft", "Sq Yd", "Sq M", "Acre".
// formatSize accepts either (size, unit) or a Property object (uses area_display
// if backend has pre-formatted it, otherwise size_sqft + size_unit).
const UNIT_LABEL = {
  sqft: "Sq Ft",
  sqyd: "Sq Yd",
  sqm:  "Sq M",
  acre: "Acre",
};

export const sizeUnitLabel = (u) => UNIT_LABEL[String(u || "").toLowerCase()] || UNIT_LABEL.sqyd;

export const formatSize = (size, unit = "sqyd") => {
  if (size && typeof size === "object") {
    // Property object passed — prefer pre-formatted area_display from backend
    if (size.area_display) return size.area_display;
    unit = size.size_unit || "sqyd";
    size = size.size_sqft;
  }
  if (size === undefined || size === null || size === "") return "—";
  const n = Math.round(Number(size));
  if (!n) return "—";
  const u = String(unit || "sqyd").toLowerCase();
  const label = UNIT_LABEL[u] || UNIT_LABEL.sqyd;
  return `${n} ${label}`;
};

export const resolveImage = (src, opts = {}) => {
  // v2.71.80 — Optional `?w=` query param to fetch a resized variant.
  // Backend `/api/files/{path}` accepts ?w=<width> and returns a WebP
  // (when Accept: image/webp) or JPEG resized to that width, capped at the
  // original. Pass `{ w: 600 }` for property cards, `{ w: 1200 }` for
  // detail-page gallery, or omit for the original.
  if (!src) return "";
  let url;
  if (src.startsWith("http")) {
    url = src;
  } else if (src.startsWith("/api/")) {
    url = `${BACKEND_URL}${src}`;
  } else {
    url = src;
  }
  const w = opts.w ? Math.max(1, Math.floor(opts.w)) : 0;
  if (!w) return url;
  // Only append to our own files; bare external URLs (Unsplash etc.) handle
  // their own resizing.
  if (!url.includes("/api/files/")) return url;
  return `${url}${url.includes("?") ? "&" : "?"}w=${w}`;
};

export const apiErrorMsg = (err, fallback = "Something went wrong") => {
  // v2.33 — prefer server-supplied detail (FastAPI HTTPException). Only show
  // a generic "Network error" when there's literally no response object —
  // i.e. the request never reached the server. This prevents the confusing
  // case where a successful 2xx was reported as "Network error" because
  // axios threw on an unrelated CORS preflight followup.
  if (err?.response) {
    const detail = err.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    if (detail) return String(detail);
    return `${fallback} (HTTP ${err.response.status})`;
  }
  // No response — true network/timeout case
  if (err?.code === "ECONNABORTED") return "Request timed out — please check your connection and retry";
  if (err?.message?.toLowerCase().includes("network")) return "Network error — please check your connection";
  return err?.message || fallback;
};
